import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../../../components/ui/dialog';
import { patientsApi } from '../../../lib/api';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  UserRound,
  Phone,
  Calendar,
  ChevronRight,
  Loader2
} from 'lucide-react';

export const PatientsPage = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // New patient form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    gender: '',  // Bebek cinsiyeti
    pregnancy_week: '',
    pregnancy_day: '',
    notes: ''
  });

  const fetchPatients = useCallback(async (search = '') => {
    try {
      setLoading(true);
      setFetchError(false);
      const response = await patientsApi.list({ search, limit: 100 });
      setPatients(response.patients);
    } catch (error) {
      setFetchError(true);
      toast.error('Hastalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Debounced search — skip initial mount
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      fetchPatients(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchPatients]);

  const handleCreatePatient = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Format pregnancy as "22w6d" style
      let pregnancyFormatted = null;
      if (formData.pregnancy_week) {
        pregnancyFormatted = `${formData.pregnancy_week}w${formData.pregnancy_day || '0'}d`;
      }

      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email || null,
        phone: formData.phone || null,
        gender: formData.gender || null,
        pregnancy_week: formData.pregnancy_week ? parseInt(formData.pregnancy_week) : null,
        pregnancy_day: formData.pregnancy_day ? parseInt(formData.pregnancy_day) : 0,
        pregnancy_formatted: pregnancyFormatted,
        notes: formData.notes || null
      };

      await patientsApi.create(payload);
      toast.success('Hasta başarıyla oluşturuldu');
      setIsModalOpen(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        gender: '',
        pregnancy_week: '',
        pregnancy_day: '',
        notes: ''
      });
      fetchPatients(searchTerm);
    } catch (error) {
      toast.error(error.message || 'Hasta oluşturulurken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <DashboardLayout title="Hastalar" subtitle="Hasta kayıtlarını yönetin">
      <div className="space-y-6" data-testid="patients-page">
        {/* Header with search and add button */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          {/* Search */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="İsme göre ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="patients-search-input"
            />
          </div>

          {/* Add Patient Button */}
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#5A67D8] hover:bg-[#4F46E5] text-white"
                data-testid="add-patient-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Hasta
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold text-slate-800">
                  Yeni Hasta Ekle
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreatePatient} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Ad *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      data-testid="patient-first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Soyad *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      data-testid="patient-last-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-posta</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="hasta@email.com"
                    data-testid="patient-email-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="05XX XXX XX XX"
                      data-testid="patient-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gender">Bebek Cinsiyeti</Label>
                    <select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="patient-gender-select"
                    >
                      <option value="">Seçiniz</option>
                      <option value="female">Kız</option>
                      <option value="male">Erkek</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pregnancy_week">Gebelik Haftası</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="pregnancy_week"
                        name="pregnancy_week"
                        type="number"
                        min="1"
                        max="42"
                        value={formData.pregnancy_week}
                        onChange={handleInputChange}
                        placeholder="22"
                        className="w-16"
                        data-testid="patient-pregnancy-week-input"
                      />
                      <span className="text-slate-500 font-medium">w</span>
                      <Input
                        id="pregnancy_day"
                        name="pregnancy_day"
                        type="number"
                        min="0"
                        max="6"
                        value={formData.pregnancy_day}
                        onChange={handleInputChange}
                        placeholder="6"
                        className="w-16"
                        data-testid="patient-pregnancy-day-input"
                      />
                      <span className="text-slate-500 font-medium">d</span>
                    </div>
                    <p className="text-xs text-slate-400">Örn: 22w6d</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notlar</Label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="patient-notes-input"
                  />
                </div>

                <DialogFooter className="mt-6">
                  <DialogClose asChild>
                    <Button type="button" variant="outline">
                      İptal
                    </Button>
                  </DialogClose>
                  <Button
                    type="submit"
                    className="bg-[#5A67D8] hover:bg-[#4F46E5] text-white"
                    disabled={submitting}
                    data-testid="patient-submit-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Kaydediliyor...
                      </>
                    ) : (
                      'Kaydet'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Patients List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#5A67D8]" />
          </div>
        ) : fetchError ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-12 text-center">
              <UserRound className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-4">Veriler yüklenemedi</p>
              <Button
                onClick={() => fetchPatients(searchTerm)}
                className="bg-[#5A67D8] hover:bg-[#4F46E5] text-white"
              >
                Tekrar Dene
              </Button>
            </CardContent>
          </Card>
        ) : patients.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-12 text-center">
              <UserRound className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">Henüz hasta kaydı yok</h3>
              <p className="text-slate-500 mb-4">İlk hastanızı ekleyerek başlayın</p>
              <Button
                onClick={() => setIsModalOpen(true)}
                className="bg-[#5A67D8] hover:bg-[#4F46E5] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Hasta Ekle
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {patients.map((patient) => (
              <Card
                key={patient.id}
                className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer group"
                onClick={() => navigate(`/doctor/patients/${patient.id}`)}
                data-testid={`patient-card-${patient.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#E0D7FF] flex items-center justify-center">
                        <UserRound className="w-6 h-6 text-[#5A67D8]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 group-hover:text-[#5A67D8] transition-colors">
                          {patient.first_name} {patient.last_name}
                        </h3>
                        {patient.pregnancy_week && (
                          <p className="text-sm text-slate-500">{patient.pregnancy_week}. hafta</p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#5A67D8] transition-colors" />
                  </div>

                  <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
                    {patient.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        <span>{patient.phone}</span>
                      </div>
                    )}
                    {patient.pregnancy_week && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{patient.pregnancy_week}. hafta</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Stats */}
        {patients.length > 0 && (
          <div className="text-center text-sm text-slate-500">
            Toplam {patients.length} hasta kaydı
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PatientsPage;
